import {Component, OnInit} from '@angular/core';
import {UserEmail} from '../../models/user-email';
import {ActivatedRoute} from '@angular/router';
import {ManageInvoiceRecipientsService} from '../../services/manage-invoice-recipients.service';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import {UserLogged} from '../../models/userLogged';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {BaseClientComponent} from '../base-client.component';

@Component({
  selector: 'app-manage-invoice-recipients',
  templateUrl: './manage-invoice-recipients.component.html',
  styleUrls: ['./manage-invoice-recipients.component.scss']
})
export class ManageInvoiceRecipientsComponent extends BaseClientComponent implements OnInit {

  EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  private companyUniqId: string;
  companyName: string;
  usersByMckayEntities: any = {};
  financeRoleFieldsetTitle: string;
  recipientsList: UserEmail[] = [];
  mbuDisplayed: boolean;
  mbuFieldsetTitle: string;
  mbuRecipientsList: UserEmail[] = [];
  mbuContracts: string;
  qduDisplayed: boolean;
  qduFieldsetTitle: string;
  qduRecipientsList: UserEmail[] = [];
  qduContracts: string;
  mbiDisplayed: boolean;
  mbiFieldsetTitle: string;
  mbiRecipientsList: UserEmail[] = [];
  mbiContracts: string;
  jsdDisplayed: boolean;
  jsdFieldsetTitle: string;
  jsdRecipientsList: UserEmail[] = [];
  jsdContracts: string;
  previousMbuRecipientsList: UserEmail[] = [];
  previousQduRecipientsList: UserEmail[] = [];
  previousMbiRecipientsList: UserEmail[] = [];
  previousJsdRecipientsList: UserEmail[] = [];
  saveDisabled = true;
  errorMessage = '';
  admins: string;
  currentUser: UserLogged;
  readOnly: boolean;
  success: boolean;
  successMessage: string;
  unusedContacts = [];
  addMckayEntityDialogDisplay = false;
  addMckayEntityForm: FormGroup;
  availableMckayEntities = [];
  selectedMckayEntities = [];
  private organizations: any;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private authenticationService: AuthenticationService,
              private utilsService: UtilsService,
              private manageInvoiceRecipientsService: ManageInvoiceRecipientsService) {
    super();
  }

  ngOnInit() {
    this.success = false;
    this.errorMessage = '';
    this.financeRoleFieldsetTitle = 'Finance roles';
    this.currentUser = this.authenticationService.userCurrent;
    this.readOnly = !this.currentUser.roleGroups.some(role => role.name === 'super' || role.name === 'admin');
    this.route.paramMap.subscribe(params => {
      this.companyUniqId = params.get('companyUniqId');
      const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
        this.initFinanceUsersAndServiceCategories();
      });
      this.subscriptions.push(response$);
    });
  }

  private initFinanceUsersAndServiceCategories() {
    this.manageInvoiceRecipientsService.getFinanceUsersAndServiceCategories(this.companyUniqId).subscribe({
      next: result => {
        if (this.currentUser.permissions.isMckayUser) {
          this.companyName = ' - ' + result['companyName'];
        }
        this.admins = result['adminNames'];
        this.recipientsList = result['users'];
        this.recipientsList.sort((a, b) => a.name.localeCompare(b.name));
        this.usersByMckayEntities = {};
        this.transformData();
        this.fillOrganizationBooleans(result['organizations']);
        this.errorMessage = '';
        this.availableMckayEntities = result['organizationsByUuid'];
        this.selectedMckayEntities = Object.keys(result['organizations']);
        this.organizations = result['organizations'];
        this.initForm();
      },
      error: error => {
        this.errorMessage = error.error.message || 'An unexpected error occurred.';
      }
    });
  }

  transformData() {
    this.recipientsList.forEach(user => {
      if (user.mckayEntities) {
        user.mckayEntities.forEach(entity => {
          if (!this.usersByMckayEntities[entity]) {
            this.usersByMckayEntities[entity] = [];
          }
          this.usersByMckayEntities[entity].push({
            id: user.id,
            name: user.name,
            email: user.email
          });
        });
      }
    });
  }

  private sortRecipientLists() {
    this.mbuRecipientsList.sort((a, b) => a.name.localeCompare(b.name));
    this.qduRecipientsList.sort((a, b) => a.name.localeCompare(b.name));
    this.mbiRecipientsList.sort((a, b) => a.name.localeCompare(b.name));
    this.jsdRecipientsList.sort((a, b) => a.name.localeCompare(b.name));
  }

  fillOrganizationBooleans(organizations) {
    this.mbuDisplayed = false;
    this.qduDisplayed = false;
    this.mbiDisplayed = false;
    this.jsdDisplayed = false;
    Object.keys(organizations).forEach(key => {
      const value = organizations[key]['shortname'];
      if (!organizations[key]['numberOfBwContracts']) {
        organizations[key]['numberOfBwContracts'] = 0;
      }
      if (!organizations[key]['numberOfMdContracts']) {
        organizations[key]['numberOfMdContracts'] = 0;
      }
      switch (value) {
        case 'MBU':
          this.mbuDisplayed = true;
          this.mbuRecipientsList = this.usersByMckayEntities['MBU'] ? this.usersByMckayEntities['MBU'] : [];
          this.mbuFieldsetTitle = organizations[key]['name'] + ' (' + organizations[key]['territory'] + ')';
          this.mbuContracts = organizations[key]['numberOfBwContracts'] + ' BW, ' + organizations[key]['numberOfMdContracts'] + ' MD contracts';
          break;
        case 'QDU':
          this.qduDisplayed = true;
          this.qduRecipientsList = this.usersByMckayEntities['QDU'] ? this.usersByMckayEntities['QDU'] : [];
          this.qduFieldsetTitle = organizations[key]['name'] + ' (' + organizations[key]['territory'] + ')';
          this.qduContracts = organizations[key]['numberOfBwContracts'] + ' BW, ' + organizations[key]['numberOfMdContracts'] + ' MD contracts';
          break;
        case 'MBI':
          this.mbiDisplayed = true;
          this.mbiRecipientsList = this.usersByMckayEntities['MBI'] ? this.usersByMckayEntities['MBI'] : [];
          this.mbiFieldsetTitle = organizations[key]['name'] + ' (' + organizations[key]['territory'] + ')';
          this.mbiContracts = organizations[key]['numberOfBwContracts'] + ' BW, ' + organizations[key]['numberOfMdContracts'] + ' MD contracts';
          break;
        case 'JSD':
          this.jsdDisplayed = true;
          this.jsdRecipientsList = this.usersByMckayEntities['JSD'] ? this.usersByMckayEntities['JSD'] : [];
          this.jsdFieldsetTitle = organizations[key]['name'] + ' (' + organizations[key]['territory'] + ')';
          this.jsdContracts = organizations[key]['numberOfBwContracts'] + ' BW, ' + organizations[key]['numberOfMdContracts'] + ' MD contracts';
          break;
        default:
          break;
      }
      this.sortRecipientLists();
      this.updatePreviousStates();
      this.calculateUnusedContacts();
    });
  }

  updatePreviousStates() {
    this.previousMbuRecipientsList = [...this.mbuRecipientsList];
    this.previousQduRecipientsList = [...this.qduRecipientsList];
    this.previousMbiRecipientsList = [...this.mbiRecipientsList];
    this.previousJsdRecipientsList = [...this.jsdRecipientsList];
  }

  arraysEquals(arr1: UserEmail[], arr2: UserEmail[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }
    const ids = new Set(arr2.map(item => item.id));
    return arr1.every(item => ids.has(item.id));
  }

  checkListChanges() {
    const lists = [
      {
        list: this.mbuRecipientsList,
        previousList: this.previousMbuRecipientsList,
        name: 'MBU',
        isDisplayed: this.mbuDisplayed
      },
      {
        list: this.qduRecipientsList,
        previousList: this.previousQduRecipientsList,
        name: 'QDU',
        isDisplayed: this.qduDisplayed
      },
      {
        list: this.mbiRecipientsList,
        previousList: this.previousMbiRecipientsList,
        name: 'MBI',
        isDisplayed: this.mbiDisplayed
      },
      {
        list: this.jsdRecipientsList,
        previousList: this.previousJsdRecipientsList,
        name: 'JSD',
        isDisplayed: this.jsdDisplayed
      }
    ];
    let anyListChanged = false;
    for (const {list, previousList, isDisplayed} of lists) {
      if (list && previousList && isDisplayed) {
        const listChanged = !this.arraysEquals(list, previousList);
        anyListChanged = anyListChanged || listChanged;
      }
    }
    this.saveDisabled = !anyListChanged;
  }

  toggleSelection(recipient: UserEmail, list: UserEmail[]) {
    this.success = false;
    this.errorMessage = '';
    if (this.isRecipientInList(recipient, list)) {
      list.splice(list.findIndex(item => item.id === recipient.id), 1);
    } else {
      list.push(recipient);
    }
    recipient.selected = !recipient.selected;
    this.checkListChanges();
    this.calculateUnusedContacts();
  }

  cancel() {
    this.mbuRecipientsList = [...this.previousMbuRecipientsList];
    this.qduRecipientsList = [...this.previousQduRecipientsList];
    this.mbiRecipientsList = [...this.previousMbiRecipientsList];
    this.jsdRecipientsList = [...this.previousJsdRecipientsList];
    this.checkListChanges();
    this.calculateUnusedContacts();
  }

  save() {
    this.manageInvoiceRecipientsService.updateFinanceUsers(this.companyUniqId, {
      'MBU': this.mbuRecipientsList.map(recipient => recipient.id),
      'QDU': this.qduRecipientsList.map(recipient => recipient.id),
      'MBI': this.mbiRecipientsList.map(recipient => recipient.id),
      'JSD': this.jsdRecipientsList.map(recipient => recipient.id)
    }).subscribe({
      next: () => {
        this.success = true;
        this.sortRecipientLists();
        this.updatePreviousStates();
        this.checkListChanges();
        this.calculateUnusedContacts();
      }, error: error => {
        this.errorMessage = error.error.message || 'An error occurred while updating finance users.';
      }
    });
  }

  isRecipientInList(recipient: UserEmail, list: UserEmail[]): boolean {
    return list.some(item => item.email === recipient.email);
  }

  getUserCurrent() {
    return this.authenticationService.getUserCurrent();
  }

  exportToXLSX(): void {
    const dynamicColumns = [];
    if (this.mbuDisplayed && this.mbuRecipientsList && this.mbuRecipientsList.length > 0) {
      dynamicColumns.push({name: 'McKay Brothers', shortName: 'MBU'});
    }
    if (this.qduDisplayed && this.qduRecipientsList && this.qduRecipientsList.length > 0) {
      dynamicColumns.push({name: 'Quincy Data', shortName: 'QDU'});
    }
    if (this.mbiDisplayed && this.mbiRecipientsList && this.mbiRecipientsList.length > 0) {
      dynamicColumns.push({name: 'Mckay Brothers International', shortName: 'MBI'});
    }
    if (this.jsdDisplayed && this.jsdRecipientsList && this.jsdRecipientsList.length > 0) {
      dynamicColumns.push({name: 'Josada', shortName: 'JSD'});
    }
    const data = this.recipientsList.map(recipient => {
      const rowData = {
        Name: recipient.name,
        Email: recipient.email
      };
      dynamicColumns.forEach(column => {
        if (this[column.shortName.toLowerCase() + 'RecipientsList']) {
          rowData[column.name] = this.isRecipientInList(recipient, this[column.shortName.toLowerCase() + 'RecipientsList']) ? 'X' : '';
        }
      });
      return rowData;
    });
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10);
    const filename = `invoice_recipients_${formattedDate}.xlsx`;
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    const workbook: XLSX.WorkBook = {
      Sheets: {'Contacts': worksheet},
      SheetNames: ['Contacts']
    };
    const excelBuffer: any = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
    const dataBlob: Blob = new Blob([excelBuffer], {type: this.EXCEL_TYPE});
    saveAs(dataBlob, filename);
  }

  calculateUnusedContacts() {
    this.unusedContacts = this.recipientsList.filter(recipient =>
      !this.isRecipientInList(recipient, this.mbuRecipientsList) &&
      !this.isRecipientInList(recipient, this.qduRecipientsList) &&
      !this.isRecipientInList(recipient, this.mbiRecipientsList) &&
      !this.isRecipientInList(recipient, this.jsdRecipientsList)
    );
  }

  getUnusedContactsTooltip() {
    return this.unusedContacts.map(contact => `<li>${contact.name} &lt;${contact.email}&gt;</li>`).join('');
  }

  selectAll(list: UserEmail[]) {
    this.recipientsList.forEach(recipient => {
      if (!this.isRecipientInList(recipient, list)) {
        list.push(recipient);
      }
      recipient.selected = true;
    });
    this.checkListChanges();
    this.calculateUnusedContacts();
  }

  deselectAll(list: UserEmail[]) {
    list.length = 0;
    this.recipientsList.forEach(recipient => {
      recipient.selected = false;
    });
    this.checkListChanges();
    this.calculateUnusedContacts();
  }

  private initForm() {
    this.addMckayEntityForm = this.formBuilder.group({});
    this.availableMckayEntitiesAsArray.forEach(entity => {
      const hasContracts = this.hasContracts(entity.uuid);
      const isSelected = this.isEntitySelected(entity.uuid);
      this.addMckayEntityForm.addControl(entity.uuid, new FormControl({value: isSelected, disabled: hasContracts}));
    });
  }

  get availableMckayEntitiesAsArray() {
    return Object.values(this.availableMckayEntities);
  }

  showAddMckayEntityDialog(): void {
    this.addMckayEntityDialogDisplay = true;
  }

  hideAddMckayEntityDialog(): void {
    this.addMckayEntityDialogDisplay = false;
  }

  isEntitySelected(entityUuid: string): boolean {
    return this.selectedMckayEntities.includes(entityUuid);
  }

  hasContracts(entityUuid: string): boolean {
    const entity = this.organizations[entityUuid];
    return entity && ((entity.numberOfMdContracts && entity.numberOfMdContracts > 0) || (entity.numberOfBwContracts && entity.numberOfBwContracts > 0));
  }

  onSubmitAddMckayEntity(): void {
    if (this.addMckayEntityForm.valid) {
      const selectedEntities = Object.keys(this.addMckayEntityForm.value)
        .filter(key => this.addMckayEntityForm.value[key] === true && !this.isEntitySelected(key));
      if (selectedEntities.length > 0) {
        this.utilsService.lockUi();
        this.manageInvoiceRecipientsService.addMckayEntitiesToCompany(this.companyUniqId, selectedEntities).subscribe({
          next: () => {
            this.utilsService.displayMessage('Success', 'Entities added successfully', 'success');
            this.utilsService.unlockUi();
            this.ngOnInit();
          },
          error: (error) => {
            this.utilsService.handleErrorWebservice$(error, 'Error adding entities', 'error');
            this.utilsService.unlockUi();
          }
        });
      }
      this.hideAddMckayEntityDialog();
    }
  }
}
